import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "url",
    "status",
    "warning",
    "error",
    "payload",
    "form",
    "address",
    "label",
    "area",
    "rooms",
    "floor",
    "buildingFloors",
    "buildingYear",
    "finish",
    "lat",
    "lng",
    "salePurpose",
    "rentPurpose",
    "primaryMarket",
    "secondaryMarket"
  ]

  static values = {
    url: String,
    importing: String,
    success: String,
    failed: String
  }

  async import(event) {
    event.preventDefault()

    const url = this.urlTarget.value.trim()
    if (!url) return

    this.clearError()
    this.clearWarning()
    this.setStatus(this.importingValue)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        credentials: "same-origin",
        body: JSON.stringify({ url })
      })

      const payload = await response.json()
      if (!response.ok || !payload.ok) {
        throw new Error(payload.error || this.failedValue)
      }

      this.applyPreview(payload)
      this.payloadTarget.value = JSON.stringify(payload)
      this.setStatus(this.successValue)
      if (payload.warnings?.length) {
        this.showWarning(payload.warnings.join(" "))
      }
    } catch (error) {
      this.showError(error.message || this.failedValue)
      this.setStatus("")
    }
  }

  applyPreview(payload) {
    const property = payload.property || {}
    const listing = payload.listing || {}

    if (this.hasAddressTarget && property.address_line) {
      this.addressTarget.value = property.address_line
    }
    if (this.hasLabelTarget && property.label) {
      this.labelTarget.value = property.label
    }
    if (this.hasAreaTarget && property.area_m2) {
      this.areaTarget.value = property.area_m2
    }
    if (this.hasRoomsTarget && property.rooms) {
      this.roomsTarget.value = property.rooms
    }
    if (this.hasFloorTarget && property.floor != null) {
      this.floorTarget.value = property.floor
    }
    if (this.hasBuildingFloorsTarget && property.building_floors) {
      this.buildingFloorsTarget.value = property.building_floors
    }
    if (this.hasBuildingYearTarget && property.building_year) {
      this.buildingYearTarget.value = property.building_year
    }
    if (this.hasFinishTarget && property.finish_condition) {
      this.finishTarget.value = property.finish_condition
    }
    if (this.hasLatTarget && property.lat != null) {
      this.latTarget.value = property.lat
    }
    if (this.hasLngTarget && property.lng != null) {
      this.lngTarget.value = property.lng
    }

    const purpose = property.purpose || listing.listing_kind
    if (purpose === "rent" && this.hasRentPurposeTarget) {
      this.rentPurposeTarget.checked = true
      this.rentPurposeTarget.dispatchEvent(new Event("change", { bubbles: true }))
    } else if (this.hasSalePurposeTarget) {
      this.salePurposeTarget.checked = true
      this.salePurposeTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    if (property.market === "primary" && this.hasPrimaryMarketTarget) {
      this.primaryMarketTarget.checked = true
    } else     if (this.hasSecondaryMarketTarget) {
      this.secondaryMarketTarget.checked = true
    }
  }

  setStatus(text) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = text
    this.statusTarget.hidden = !text
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.hidden = false
  }

  showWarning(message) {
    if (!this.hasWarningTarget) return
    this.warningTarget.textContent = message
    this.warningTarget.hidden = false
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.errorTarget.hidden = true
  }

  clearWarning() {
    if (!this.hasWarningTarget) return
    this.warningTarget.textContent = ""
    this.warningTarget.hidden = true
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }
}
